import { Link, NavLink } from 'react-router';
import { canUseManagement } from '@/auth/roles';
import { useStaffSession } from '@/auth/react';
import { Icon } from '@/components/Icon';
import type { IconName } from '@/components/Icon';
import { cn } from '@/lib/cn';

export interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  /** Small count shown on the tab, for example items in the cart. Hidden when zero or absent. */
  badge?: number;
  /** Match this path exactly (for an index route such as `/manage`), not every path under it. */
  end?: boolean;
}

/** Bottom tab bar for phones: the active tab is burgundy with a short bar above its icon. */
export function BottomNav({ items, label }: { items: NavItem[]; label: string }) {
  return (
    <nav
      aria-label={label}
      className="sticky bottom-0 z-20 border-t border-line bg-white pb-[env(safe-area-inset-bottom)] shadow-sheet"
    >
      <ul className="mx-auto flex max-w-md">
        {items.map((item) => (
          <li key={item.to} className="flex-1">
            <NavLink
              to={item.to}
              end={item.end}
              aria-label={
                item.badge !== undefined && item.badge > 0
                  ? `${item.label}, ${item.badge} ${item.badge === 1 ? 'item' : 'items'}`
                  : undefined
              }
              className={({ isActive }) =>
                cn(
                  'relative flex min-h-14 flex-col items-center justify-center gap-0.5 text-[11px] font-semibold tracking-wide no-underline',
                  isActive ? 'text-primary' : 'text-muted hover:text-ink',
                )
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && (
                    <span
                      aria-hidden="true"
                      className="absolute top-0 left-1/2 h-0.5 w-8 -translate-x-1/2 rounded-b-full bg-primary"
                    />
                  )}
                  <span className="relative">
                    <Icon name={item.icon} />
                    {item.badge !== undefined && item.badge > 0 && (
                      <span
                        aria-hidden="true"
                        className="absolute -top-1.5 -right-3 grid min-w-5 place-items-center rounded-full bg-primary px-1 text-[11px] leading-5 font-semibold text-white tabular-nums"
                      >
                        {item.badge}
                      </span>
                    )}
                  </span>
                  {item.label}
                </>
              )}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}

/** Vertical rail for tablet and desktop: the active item is burgundy with a bar on its left edge. */
export function SideNav({ items, label }: { items: NavItem[]; label: string }) {
  return (
    <nav aria-label={label}>
      <ul className="flex flex-col gap-0.5">
        {items.map((item) => (
          <li key={item.to}>
            <NavLink
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                cn(
                  'flex min-h-11 items-center gap-3 rounded-r-control border-l-[3px] px-3 text-sm font-medium no-underline',
                  isActive
                    ? 'border-primary bg-primary-soft text-primary'
                    : 'border-transparent text-ink hover:bg-surface',
                )
              }
            >
              <Icon name={item.icon} />
              {item.label}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}

/** Tab strip under a header on phones (`md:hidden`): links drawn like a segmented control. */
export function StripNav({ items, label }: { items: NavItem[]; label: string }) {
  return (
    <nav aria-label={label} className="border-b border-line bg-white px-3 py-2 md:hidden">
      <ul className="flex gap-1 rounded-control bg-surface p-1">
        {items.map((item) => (
          <li key={item.to} className="flex-1">
            <NavLink
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                cn(
                  'flex min-h-11 items-center justify-center gap-2 rounded-[6px] px-2 text-sm font-semibold no-underline',
                  isActive ? 'bg-white text-ink shadow-card' : 'text-muted hover:text-ink',
                )
              }
            >
              <Icon name={item.icon} size={18} />
              {item.label}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}

export function SkipLink() {
  return (
    <a
      href="#main"
      className="sr-only rounded-control bg-white px-4 py-2 font-semibold focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[70]"
    >
      Skip to content
    </a>
  );
}

/**
 * The other branch screens a branch manager may also use (the backend accepts the manager on each). The
 * management overview links to them, and each of them links back to the dashboard (`ManagerHomeLink`).
 */
export const MANAGER_BRANCH_SCREENS: NavItem[] = [
  { to: '/kitchen', label: 'Kitchen', icon: 'orders' },
  { to: '/counter', label: 'Counter', icon: 'bill' },
  { to: '/staff', label: 'Waiter floor', icon: 'floor' },
];

/**
 * "Overview" in the header of a shared screen (kitchen, counter, waiter), shown to a branch manager only, so
 * the manager can always get back to `/manage`, which is titled Overview. A plain link: one history entry, Back still works. Every
 * other role sees nothing. `tone="light"` is for the dark staff header.
 */
export function ManagerHomeLink({ tone = 'dark' }: { tone?: 'dark' | 'light' }) {
  const role = useStaffSession()?.user.role;
  if (!role || !canUseManagement(role)) return null;
  return (
    <Link
      to="/manage"
      aria-label="Back to the management overview"
      className={cn(
        'inline-flex min-h-11 items-center gap-1 rounded-control px-2 text-sm font-semibold whitespace-nowrap no-underline',
        tone === 'light'
          ? 'text-white/90 hover:bg-white/10 focus-visible:outline-white'
          : 'text-primary hover:bg-primary-soft',
      )}
    >
      <span aria-hidden="true">‹</span>
      Overview
    </Link>
  );
}
